import {
  Check,
  Save,
  X,
} from "lucide-react";
import {
  AutocompleteSelect,
} from "../../shared/components/AutocompleteSelect";
import {
  planningRequestCategoryOptions,
} from "../procurement/procurement-options";
import type {
  useSuppliersWorkspace,
} from "./useSuppliersWorkspace";

export function SupplierComposer({ model }: { model: ReturnType<typeof useSuppliersWorkspace>; }) {
  const {
    composerOpen,
    setComposerOpen,
    form,
    setForm,
    contactRoleDescriptions,
    createSupplier,
  } = model;
  return (<>
    {composerOpen ? (
      <section
        className="inline-form-card"
        role="dialog"
        aria-modal="true"
        aria-label="Tedarikçi kaydı oluştur"
      >
        <div className="section-heading compact">
          <div>
            <span className="section-kicker">Yeni iş ortağı</span>
            <h2>Tedarikçi kaydı oluştur</h2>
          </div>
          <button
            className="icon-button"
            aria-label="Tedarikçi formunu kapat"
            onClick={() => setComposerOpen(false)}
          >
            <X size={17} />
          </button>
        </div>
        <div className="inline-form-grid supplier-create-form">
          <section
            className="supplier-form-group"
            aria-labelledby="supplier-identity-heading"
          >
            <header>
              <span>1</span>
              <div>
                <h3 id="supplier-identity-heading">
                  Firma ve vergi bilgileri
                </h3>
                <p>
                  Firmanın resmî kaydını ve ERP eşleştirme bilgilerini girin.
                </p>
              </div>
            </header>
            <div className="supplier-form-group-grid">
              <label>
                Tedarikçi adı <em>Zorunlu</em>
                <input
                  value={form.name}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                />
                <small>
                  Uygulamadaki listelerde görünecek kısa firma adı.
                </small>
              </label>
              <label>
                Resmî ticari unvan <em>Zorunlu</em>
                <input
                  value={form.legalName}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      legalName: event.target.value,
                    }))
                  }
                />
                <small>Vergi levhası veya faturada yazan tam unvan.</small>
              </label>
              <label>
                Vergi ülkesi <em>Zorunlu</em>
                <input
                  maxLength={2}
                  value={form.countryCode}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      countryCode: event.target.value.toUpperCase(),
                    }))
                  }
                />
                <small>İki harfli ülke kodu; Türkiye için TR.</small>
              </label>
              <label>
                Vergi numarası <em>Zorunlu</em>
                <input
                  value={form.taxNumber}
                  inputMode="numeric"
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      taxNumber: event.target.value,
                    }))
                  }
                />
                <small>
                  Aynı firmanın tekrar açılmasını önlemek için kullanılır.
                </small>
              </label>
              <label>
                Mikro / ERP tedarikçi kodu <em>Opsiyonel</em>
                <input
                  value={form.code}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      code: event.target.value,
                    }))
                  }
                />
                <small>
                  Mikro’da cari kod açıldıysa girin; daha sonra da
                  eklenebilir.
                </small>
              </label>
              <label>
                Adres <em>Opsiyonel</em>
                <input
                  value={form.addressLine}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      addressLine: event.target.value,
                    }))
                  }
                />
              </label>
            </div>
          </section>

          <section
            className="supplier-form-group"
            aria-labelledby="supplier-scope-heading"
          >
            <header>
              <span>2</span>
              <div>
                <h3 id="supplier-scope-heading">Satın alma kapsamı</h3>
                <p>
                  Bu tedarikçinin hangi ürün taleplerinde aday olacağını
                  belirleyin.
                </p>
              </div>
            </header>
            <div className="supplier-form-group-grid">
              <AutocompleteSelect
                label="Ana kategori · Zorunlu"
                value={form.category}
                options={planningRequestCategoryOptions}
                placeholder="Hammadde veya ambalaj seçin"
                onChange={(category) =>
                  setForm((current) => ({
                    ...current,
                    category,
                    includeOtherCategory: false,
                  }))
                }
              />
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={form.includeOtherCategory}
                  disabled={!form.category}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      includeOtherCategory: event.target.checked,
                    }))
                  }
                />
                <span className="checkbox-visual" aria-hidden="true">
                  {form.includeOtherCategory ? <Check size={13} /> : null}
                </span>
                <span>
                  Hammadde ve ambalajın ikisinde de çalışır
                  <small>
                    Açıksa tedarikçi iki kategorideki taleplerde de aday olur.
                  </small>
                </span>
              </label>
              <label>
                Ürün grubu kodu <em>Zorunlu</em>
                <input
                  value={form.productGroupCode}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      productGroupCode: event.target.value,
                    }))
                  }
                />
                <small>Mikro/ERP’de kullanılan kod; ör. GENEL-AMBALAJ.</small>
              </label>
              <label>
                Ürün grubu adı <em>Zorunlu</em>
                <input
                  value={form.productGroupName}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      productGroupName: event.target.value,
                    }))
                  }
                />
                <small>
                  Satın almacının tedarikçi kapsamlarında göreceği ad.
                </small>
              </label>
            </div>
          </section>

          <section
            className="supplier-form-group"
            aria-labelledby="supplier-contact-heading"
          >
            <header>
              <span>3</span>
              <div>
                <h3 id="supplier-contact-heading">
                  Birincil iletişim kişisi
                </h3>
                <p>
                  Opsiyoneldir; ilk yetkiliyi şimdi veya tedarikçi detayından
                  daha sonra ekleyebilirsiniz.
                </p>
              </div>
            </header>
            <div className="supplier-form-group-grid">
              <label>
                Ad soyad
                <input
                  value={form.contactName}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      contactName: event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Kurumsal e-posta
                <input
                  type="email"
                  value={form.contactEmail}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      contactEmail: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="form-field-wide">
                Bu kişi hangi süreçten sorumlu?
                <select
                  value={form.contactRole}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      contactRole: event.target.value,
                    }))
                  }
                >
                  <option value="RFQ">Teklif ve fiyatlandırma</option>
                  <option value="ORDER">Sipariş ve teslimat</option>
                  <option value="QUALITY">Kalite ve belgeler</option>
                  <option value="CONTRACT">Sözleşmeler</option>
                </select>
                <small>{contactRoleDescriptions[form.contactRole]}</small>
              </label>
            </div>
          </section>
        </div>
        <div className="inline-form-actions">
          <button
            className="secondary-button"
            onClick={() => setComposerOpen(false)}
          >
            Vazgeç
          </button>
          <button className="approve-button" onClick={createSupplier}>
            <Save size={15} /> Tedarikçiyi kaydet
          </button>
        </div>
      </section>
    ) : null}
  </>);
}
